<script setup lang="ts">
import { computed } from 'vue'

/**
 * Floating menu panel (the dropdown/context-menu surface). Single-root <div> so the consumer's
 * positioning (`:style` with fixed coords), `data-*-menu` attr (for outside-click detection),
 * `v-if`/`@click.stop` and width/max-height classes all fall through — the panel only owns the
 * surface styling. Pair with BaseMenuItem for the rows. Wrap in <Teleport to="body"> at the call
 * site (positioning stays with the consumer that measures the trigger).
 *
 * `variant`: default (solid, shadow-lg) · glass (translucent, blur — the map filter dropdowns).
 * `scroll`: long lists (date/distance/collection) that need overflow-y-auto + a max-height class.
 */
interface Props {
  variant?: 'default' | 'glass'
  scroll?: boolean
  /** Floating (fixed + z-9999, the teleported default) vs static (consumer positions, or inline). */
  floating?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  scroll: false,
  floating: true,
})

const panelClass = computed(() => [
  'py-1',
  props.floating ? 'fixed z-9999' : '',
  props.scroll ? 'overflow-y-auto' : 'overflow-hidden',
  props.variant === 'glass'
    ? 'rounded-xl border border-black/8 bg-white/92 shadow-md backdrop-blur-sm'
    : 'rounded-md border border-gray-200 bg-white shadow-lg',
])
</script>

<template>
  <div :class="panelClass">
    <slot />
  </div>
</template>
